import { useEffect, useMemo, useState } from "react";
import { Lock, LogOut, Download, RefreshCw, Search, Mail, ShieldCheck, Loader2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "stoicos_admin_token";

/**
 * Hidden waitlist admin dashboard.
 * Routed at /admin/waitlist (not linked from the public site).
 * Auth: single ADMIN_TOKEN env var on the backend, compared via Bearer header.
 */
export default function WaitlistAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [query, setQuery] = useState("");

  const authed = !!token;

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    setAuthError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        setAuthError("Invalid admin token. Please sign in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setData(null);
        return;
      }
      if (!res.ok) {
        setAuthError(`Request failed (${res.status})`);
        return;
      }
      setData(await res.json());
    } catch (_) {
      setAuthError("Network error. Is the backend reachable?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "StoicOS · Waitlist Admin";
    if (token) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSignIn = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
    setTokenInput("");
  };

  const handleSignOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
    setAuthError("");
  };

  const handleExport = () => {
    // Trigger a CSV download via a temporary anchor with the token in URL fragment
    // (we can't set headers on direct downloads; so we fetch+blob instead)
    fetch(`${API_BASE}/api/admin/waitlist.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Download failed");
        const dispo = r.headers.get("content-disposition") || "";
        const m = dispo.match(/filename="([^"]+)"/);
        const filename = m ? m[1] : "stoicos_waitlist.csv";
        return r.blob().then((b) => ({ blob: b, filename }));
      })
      .then(({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert("CSV export failed. Please try again."));
  };

  const filtered = useMemo(() => {
    if (!data?.subscribers) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.subscribers;
    return data.subscribers.filter(
      (s) =>
        (s.email || "").toLowerCase().includes(q) ||
        (s.sources || []).some((src) => src.toLowerCase().includes(q))
    );
  }, [data, query]);

  // ============ SIGN-IN VIEW ============
  if (!authed) {
    return (
      <main data-testid="admin-signin" className="min-h-[80vh] flex items-center justify-center px-6 pt-32 pb-24">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-gold">Admin</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-bone tracking-tight leading-tight">
            Waitlist <span className="italic text-gold">Console.</span>
          </h1>
          <p className="mt-4 text-fog font-light leading-relaxed">
            Enter your admin token to view and export the pre-launch waitlist.
          </p>

          <form onSubmit={handleSignIn} className="mt-10 space-y-5">
            <label className="block">
              <span className="text-[10px] tracking-[0.28em] uppercase text-gold mb-2 block">Admin token</span>
              <div className="relative">
                <Lock size={14} color="#C5A059" strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  required
                  autoComplete="off"
                  autoFocus
                  data-testid="admin-token-input"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  className="w-full bg-transparent border border-white/15 focus:border-gold outline-none py-3 pl-9 pr-4 text-bone font-mono placeholder:text-fog/40 transition-colors"
                  placeholder="Paste token here"
                />
              </div>
            </label>
            {authError && (
              <p data-testid="admin-signin-error" className="text-sm text-[#E89B6B] font-light">{authError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-signin-submit"
              className="w-full bg-gold hover:bg-gold-hover text-ink py-3.5 text-[12px] tracking-[0.28em] uppercase font-medium transition-colors"
            >
              Sign In
            </button>
          </form>

          <p className="mt-8 text-xs text-fog/60 leading-relaxed">
            This page is private. The token is configured server-side as the <code className="text-gold">ADMIN_TOKEN</code> environment variable.
          </p>
        </div>
      </main>
    );
  }

  // ============ DASHBOARD VIEW ============
  return (
    <main data-testid="admin-dashboard" className="min-h-[90vh] pt-28 pb-20 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={16} color="#C5A059" strokeWidth={1.5} />
              <span className="text-[11px] tracking-[0.3em] uppercase text-gold">Waitlist Console</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-bone tracking-tight">
              Virtue Guide <span className="italic text-gold">subscribers.</span>
            </h1>
            <p className="mt-2 text-fog font-light">
              Everyone awaiting launch, captured from across the site.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              disabled={loading}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-gold text-bone hover:text-gold px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} strokeWidth={1.5} />}
              Refresh
            </button>
            <button
              onClick={handleExport}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-ink px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase font-medium transition-colors"
            >
              <Download size={14} strokeWidth={1.5} /> Export CSV
            </button>
            <button
              onClick={handleSignOut}
              data-testid="admin-signout"
              aria-label="Sign out"
              className="p-2.5 text-fog hover:text-gold transition-colors"
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-10">
          <StatBlock label="Total subscribers" value={data?.total ?? "—"} testid="stat-total" />
          <StatBlock label="From App Store" value={(data?.source_counts?.app_store) ?? 0} testid="stat-app-store" />
          <StatBlock label="From Google Play" value={(data?.source_counts?.google_play) ?? 0} testid="stat-google-play" />
          <StatBlock label="From Free Stoic Quote" value={(data?.source_counts?.free_stoic_quote) ?? 0} testid="stat-free-quote" />
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-md">
          <Search size={14} color="#C5A059" strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            data-testid="admin-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email or source..."
            className="w-full bg-transparent border border-white/15 focus:border-gold outline-none py-2.5 pl-9 pr-4 text-bone placeholder:text-fog/40 text-sm transition-colors"
          />
        </div>

        {/* Table */}
        <div className="border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-ink-elevated border-b border-white/10 text-fog">
                  <th className="text-left py-3 px-4 text-[10px] tracking-[0.25em] uppercase font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-[10px] tracking-[0.25em] uppercase font-medium">Sources</th>
                  <th className="text-left py-3 px-4 text-[10px] tracking-[0.25em] uppercase font-medium">Subscribed</th>
                  <th className="text-left py-3 px-4 text-[10px] tracking-[0.25em] uppercase font-medium">Last seen</th>
                </tr>
              </thead>
              <tbody data-testid="admin-table-body">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-12 text-fog/70 font-light">
                      <Mail size={20} color="#C5A059" strokeWidth={1.5} className="inline-block mr-2 align-text-bottom opacity-60" />
                      {data?.total === 0
                        ? "No subscribers yet. Once someone joins the waitlist, they'll appear here."
                        : "No subscribers match your search."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => (
                    <tr key={s.email} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 text-bone font-mono text-[13px]">{s.email}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1.5">
                          {(s.sources || []).map((src) => (
                            <span key={src} className="text-[10px] tracking-[0.18em] uppercase text-gold border border-gold/30 px-2 py-0.5">
                              {src.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-fog text-xs">{formatDate(s.created_at)}</td>
                      <td className="py-3 px-4 text-fog text-xs">{formatDate(s.last_seen_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filtered.length > 0 && (
          <p className="mt-4 text-xs tracking-[0.18em] uppercase text-fog/70">
            Showing {filtered.length} of {data?.total ?? filtered.length} {data?.total === 1 ? "subscriber" : "subscribers"}
          </p>
        )}
      </div>
    </main>
  );
}

function StatBlock({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-ink p-6">
      <p className="text-[10px] tracking-[0.25em] uppercase text-gold">{label}</p>
      <p className="mt-3 font-serif text-3xl md:text-4xl text-bone">{value}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}
